import React from "react";
import { Activity, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const EMPTY_SF = { count: 0, mean_error: 0, suggested_a_constant_delta: 0, by_lens: [], min_followups: 3 };
const signed = (v, dp = 2) => `${v >= 0 ? "+" : ""}${v.toFixed(dp)}`;

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-mono text-sm text-slate-900">{value}</span>
    </div>
  );
}

function Badge({ badge, isLens, hasData }) {
  return (
    <button
      data-testid="surgeon-factor-badge"
      className={
        "flex items-center gap-1.5 rounded-full border px-2.5 sm:px-3 py-1.5 text-xs font-medium transition whitespace-nowrap " +
        (hasData
          ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
          : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100")
      }
    >
      <Activity className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{isLens ? "Lens SF" : "SF"} </span>n={badge.count}
      {hasData && <span className="font-mono">{signed(badge.mean_error)} D</span>}
    </button>
  );
}

function LensPanel({ lens, lensName, minFollowups, onApplyLensSF, onApplyLensC }) {
  if (!lens) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 p-2.5 text-xs text-slate-500" data-testid="lens-sf-empty">
        No follow-ups yet for <span className="font-medium text-slate-700">{lensName || "the selected lens"}</span>.
      </div>
    );
  }
  const remaining = minFollowups - lens.count;
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 p-2.5 space-y-1.5" data-testid="lens-sf-panel">
      <div className="text-[10px] uppercase tracking-wider text-sky-800 font-semibold truncate">Selected lens · {lens.lens_name}</div>
      <Row label="Follow-ups" value={`${lens.count} / ${minFollowups} needed`} />
      <Row label="Mean error" value={`${signed(lens.mean_error)} D`} />
      <Row label="A-const nudge" value={signed(lens.suggested_a_constant_delta)} />
      {lens.learned_c_constant != null && (
        <Row
          label="Learned C-constant"
          value={<span data-testid="lens-learned-c">{lens.learned_c_constant.toFixed(3)} <span className="text-slate-400">(n={lens.c_samples})</span></span>}
        />
      )}
      <div className="grid grid-cols-2 gap-1.5">
        <Button size="sm" className="bg-sky-700 hover:bg-sky-800 text-white disabled:opacity-60"
                onClick={onApplyLensSF} disabled={!lens.nudge_ready} data-testid="apply-lens-sf-btn">
          {lens.nudge_ready ? "Apply A-const nudge" : `Need ${remaining} more`}
        </Button>
        <Button size="sm" variant="outline" className="border-sky-300 text-sky-900 disabled:opacity-60"
                onClick={onApplyLensC} disabled={!lens.c_ready} data-testid="apply-lens-c-btn">
          {lens.c_ready ? "Apply learned C" : "Learned C pending"}
        </Button>
      </div>
    </div>
  );
}

function LensTable({ rows }) {
  const tone = (ready) => "px-2 py-1 text-right font-mono " + (ready ? "text-emerald-700" : "text-slate-400");
  return (
    <>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-1">Per-lens breakdown</div>
      <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200" data-testid="lens-sf-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              <th className="text-left font-medium px-2 py-1">Lens</th>
              <th className="text-right font-medium px-2 py-1">n</th>
              <th className="text-right font-medium px-2 py-1">Mean PE</th>
              <th className="text-right font-medium px-2 py-1">Nudge</th>
              <th className="text-right font-medium px-2 py-1">C</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.lens_id} className="border-t border-slate-100" data-testid={`lens-sf-row-${r.lens_id}`}>
                <td className="px-2 py-1 truncate max-w-[11rem]">{r.lens_name}</td>
                <td className="px-2 py-1 text-right font-mono">{r.count}</td>
                <td className="px-2 py-1 text-right font-mono">{signed(r.mean_error)}</td>
                <td className={tone(r.nudge_ready)}>{signed(r.suggested_a_constant_delta)}</td>
                <td className={tone(r.c_ready)}>{r.learned_c_constant != null ? r.learned_c_constant.toFixed(2) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function GlobalPanel({ sf, onApplySF, onOpenTrends }) {
  return (
    <div className="border-t border-slate-200 pt-2 space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Global (all lenses)</div>
      <Row label="Follow-ups" value={sf.count} />
      <Row label="Mean error" value={`${signed(sf.mean_error)} D`} />
      <Row label="Suggested A-const nudge" value={signed(sf.suggested_a_constant_delta)} />
      <Button size="sm" variant="outline" className="w-full" onClick={onApplySF} data-testid="apply-sf-btn">
        Apply global nudge
      </Button>
      <Button size="sm" variant="ghost" className="w-full text-sky-800 hover:bg-sky-50" onClick={onOpenTrends} data-testid="open-trends-btn">
        <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> View outcome trend chart
      </Button>
    </div>
  );
}

export default function SurgeonFactorPopover({
  surgeonFactor, currentLensSF, currentLensName, onApplySF, onApplyLensSF, onApplyLensC, onOpenTrends,
}) {
  const sf = surgeonFactor || EMPTY_SF;
  const hasData = sf.count > 0;
  const badgeIsLens = Boolean(currentLensSF && currentLensSF.count > 0);
  const badge = badgeIsLens ? currentLensSF : sf;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge badge={badge} isLens={badgeIsLens} hasData={hasData} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem]" data-testid="surgeon-factor-popover">
        <div className="text-sm font-semibold">Personalised surgeon factor</div>
        <div className="text-xs text-slate-500 mt-1">
          Prediction error (achieved − target) from follow-ups logged in History, grouped per IOL model.
        </div>
        {hasData ? (
          <div className="mt-3 space-y-2 text-sm">
            <LensPanel lens={currentLensSF} lensName={currentLensName} minFollowups={sf.min_followups}
                       onApplyLensSF={onApplyLensSF} onApplyLensC={onApplyLensC} />
            <LensTable rows={sf.by_lens || []} />
            <GlobalPanel sf={sf} onApplySF={onApplySF} onOpenTrends={onOpenTrends} />
          </div>
        ) : (
          <div className="mt-3 text-xs text-slate-500">
            Log at least one post-op refraction in the History drawer to start seeing your surgeon factor.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
